import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  mitigatorActions,
  getCurrentSource,
  getRisksList,
} from "../../../store/";

const getSourceFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Source ID",
      labelAlign: "top",
      attribute: "source_id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-81-px", input: "pl-12" },
    });
  }
  fields.push(
    {
      label: "Source",
      labelAlign: "top",
      attribute: "name",
      placeholder: "Source",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-81-px", input: "pl-12" },
    },
    {
      label: "Description",
      labelAlign: "top",
      attribute: "description",
      placeholder: "Description",
      rows: 3,
      typeRepresentation: "TEXTAREA",
      classes: { root: "w-100", label: "text-right w-81-px" },
    },
  );
  return fields;
};
export const CreateNewSource = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = useState(false);
  const _ref = useRef(null);
  const viewRef = useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab");
  const sourceId = params.get("id");
  const currentSource = useSelector(getCurrentSource);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (currentSource && viewRef.current) {
      viewRef.current.updateFormData({
        source_id: currentSource.source_id,
        name: currentSource.name,
        description: currentSource.description,
      });
    }
  }, [currentSource]);

  useEffect(() => {
    if (!sourceId) return;
    dispatch(mitigatorActions.fetchSource({ source_id: sourceId }));
  }, [sourceId]);

  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      name: values.name,
      description: values.description,
    };
    const action = isEditMode
      ? mitigatorActions.updateSource({
          id: sourceId,
          status: currentSource?.status,
          ...body,
        })
      : mitigatorActions.createSource(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {sourceId && currentSource && !isEditMode ? (
        <>
          <Row justify="between" className="w-100 pl-16 pr-16 mt-36">
            <span className="ff-exo2 fw-500 f22 c238787">Source</span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>

          {/* VIEW MODE FORM */}
          <NMForm
            className="w-100 pl-30 pr-26 mt-24"
            ref={viewRef}
            readOnly={true}
            fields={getSourceFields(true, false)}
            formData={{
              source_id: currentSource.source_id,
              name: currentSource.name,
              description: currentSource.description,
            }}
          />

          <Row
            justify="end"
            className="w-100 pr-36 mt-16"
            style={{ gap: "12px" }}
          >
            {currentTab === "draft" && (
              <>
                <Button
                  label="Edit"
                  icon="EDIT"
                  onClick={() => setIsEditMode(true)}
                />
                <Button
                  label="Activate"
                  icon="CHECK_CONFIRM"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateSource({
                        id: sourceId,
                        name: currentSource.name,
                        description: currentSource.description,
                        status: "active",
                      }),
                    );

                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}

            {currentTab === "active" && (
              <Button
                label="Archive"
                icon="OPEN_FOLDER"
                variant="CAUTION"
                onClick={() => {
                  dispatch(
                    mitigatorActions.updateSource({
                      id: sourceId,
                      name: currentSource.name,
                      description: currentSource.description,
                      status: "archive",
                    }),
                  );

                  publish("MITIGATOR_HIDE_NEW_CONTENT");
                }}
              />
            )}
            {currentTab === "archive" && (
              <Button
                label="Unarchive"
                icon="OPEN_FOLDER"
                onClick={() => {
                  dispatch(
                    mitigatorActions.updateSource({
                      id: sourceId,
                      name: currentSource.name,
                      description: currentSource.description,
                      status: "draft",
                    }),
                  );

                  publish("MITIGATOR_HIDE_NEW_CONTENT");
                }}
              />
            )}
          </Row>
        </>
      ) : (
        <>
          <Row justify="between" className="w-100 pl-16 pr-16 mt-36">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Source
            </span>
          </Row>

          {/* EDIT/CREATE FORM */}
          <NMForm
            className="w-100 pl-30 pr-26"
            ref={_ref}
            fields={
              isEditMode
                ? getSourceFields(false, true)
                : getSourceFields(false, false)
            }
            formData={
              isEditMode
                ? {
                    source_id: currentSource?.source_id,
                    name: currentSource?.name,
                    description: currentSource?.description,
                  }
                : {
                    source_id: "",
                    name: "",
                    description: "",
                  }
            }
            id="new-source-form"
          />

          <Row
            className="pr-26 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            <OutlinedButton
              label="Cancel"
              icon="CANCEL"
              onClick={handleCancel}
              disabled={submitting}
            />
            <Button
              label="Save"
              icon="SAVE"
              onClick={handleSave}
              loading={submitting}
            />
          </Row>
        </>
      )}
    </div>
  );
};
